import React, { useState } from 'react';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { DateRangePicker } from 'react-dates';
import moment from 'moment';
import { ReactComponent as HokErrorIcon } from '../../../img/icons/hok-error.svg';
import { formatDateTime, FORMAT_STANDARD_DATE, yearListRender } from '../../../constants/dateFormatting';
import { TICKET_ACTION_STATUS_ACTION_REQUIRED, TICKET_STATUS } from '../../../constants/ticketing';
import { useContactFormSubjects } from '../../../utils/customHooks';
import { get } from 'lodash-es';

const TICKET_ID_COLUMN = {
  headerClassName: 'react-table-headers',
  className: 'react-table-cells',
  minWidth: 130,
  Header: <FormattedMessage id="adminTicketListPage.columns.id" />,
  accessor: 'number',
};

const REQUIRED_ACTION_COLUMN = {
  headerClassName: 'react-table-headers',
  className: 'react-table-cells',
  minWidth: 120,
  id: 'actionStatusOr',
  accessor: 'actionStatus',
  Header: <FormattedMessage id="adminTicketListPage.columns.requiredAction" />,
  Cell: rowInfo => (
    <div className="d-flex align-items-center justify-content-center h-100">
      {rowInfo.original.actionStatus === TICKET_ACTION_STATUS_ACTION_REQUIRED ? (
        <HokErrorIcon className="svg-table-icons svg-orange" />
      ) : null}
    </div>
  ),
  Filter: injectIntl(({ filter, onChange, intl }) => (
    <select
      style={{ width: '100%' }}
      onChange={event => onChange(event.target.value)}
      value={filter ? filter.value : ''}>
      <option value="">{intl.formatMessage({ id: 'adminTicketListPage.actionFilter.all' })}</option>
      <option value={TICKET_ACTION_STATUS_ACTION_REQUIRED}>
        {intl.formatMessage({ id: 'adminTicketListPage.actionFilter.yes' })}
      </option>
      <option value="NO">{intl.formatMessage({ id: 'adminTicketListPage.actionFilter.no' })}</option>
    </select>
  )),
};

const FIRST_NAME_COLUMN = {
  headerClassName: 'react-table-headers',
  className: 'react-table-cells',
  minWidth: 120,
  Header: <FormattedMessage id="adminTicketListPage.columns.firstName" />,
  accessor: 'firstName',
};

const LAST_NAME_COLUMN = {
  headerClassName: 'react-table-headers',
  className: 'react-table-cells',
  minWidth: 120,
  Header: <FormattedMessage id="adminTicketListPage.columns.lastName" />,
  accessor: 'lastName',
};

const TITLE_COLUMN = {
  headerClassName: 'react-table-headers',
  className: 'react-table-cells',
  style: { cursor: 'pointer' },
  id: 'subject',
  accessor: 'subject',
  minWidth: 150,
  Header: <FormattedMessage id="adminTicketListPage.columns.title" />,
  Cell: rowInfo => (
    <div>
      {rowInfo.original.subject.length > 20
        ? `${rowInfo.original.subject.substring(0, 20).trim()}...`
        : rowInfo.original.subject}
    </div>
  ),
};

const TYPE_COLUMN = {
  headerClassName: 'react-table-headers',
  className: 'react-table-cells',
  minWidth: 120,
  id: 'type',
  accessor: 'type',
  Header: <FormattedMessage id="adminTicketListPage.columns.type" />,
  Filter: ({ filter, onChange }) => {
    const { formatMessage } = useIntl();
    const { subjects, loading } = useContactFormSubjects();
    return (
      <select
        style={{ width: '100%' }}
        onChange={event => onChange(event.target.value)}
        value={filter ? filter.value : ''}>
        <option value="">{formatMessage({ id: 'adminTicketListPage.typeFilter.all' })}</option>
        {loading && (
          <option disabled value="">
            {formatMessage({ id: 'loading' })}
          </option>
        )}
        {subjects.map(subject => (
          <option key={subject.id} value={subject.id}>
            {subject.subject}
          </option>
        ))}
      </select>
    );
  },
  Cell: rowInfo => {
    const { subjects } = useContactFormSubjects();

    let cellValue = rowInfo.original.type.replace(/_/g, ' ');
    cellValue = cellValue.charAt(0) + cellValue.substring(1).toLowerCase();
    if (subjects.length > 0) {
      cellValue = subjects.find(subject => subject.id === rowInfo.original.type).subject;
    }
    return <div>{cellValue}</div>;
  },
};

const STATUS_COLUMN = {
  headerClassName: 'react-table-headers',
  className: 'react-table-cells',
  minWidth: 100,
  id: 'status',
  accessor: 'status',
  Header: <FormattedMessage id="adminTicketListPage.columns.status" />,
  Filter: injectIntl(({ filter, onChange, intl }) => (
    <select
      style={{ width: '100%' }}
      onChange={event => onChange(event.target.value)}
      value={filter ? filter.value : ''}>
      <option value="">{intl.formatMessage({ id: 'adminTicketListPage.statusFilter.all' })}</option>
      {TICKET_STATUS.map(status => (
        <option key={status} value={status}>
          {intl.formatMessage({ id: `adminTicketListPage.statusFilter.${status}` })}
        </option>
      ))}
    </select>
  )),
  Cell: rowInfo => {
    const { formatMessage } = useIntl();
    return <div>{formatMessage({ id: `adminTicketListPage.statusFilter.${rowInfo.original.status}` })}</div>;
  },
};

const CREATION_TIMESTAMP_COLUMN = {
  headerClassName: 'react-table-headers',
  className: 'react-table-cells',
  headerStyle: { fontWeight: '500' },
  style: { fontWeight: '500' },
  minWidth: 250,
  id: 'createdAtInterval',
  accessor: 'createdAt',
  Header: <FormattedMessage id="adminTicketListPage.columns.creationTimestamp" />,
  Cell: rowInfo => <div>{formatDateTime(rowInfo.original.createdAt, false)}</div>,
  Filter: injectIntl(({ filter, onChange, intl }) => {
    const [inputFocused, setInputFocused] = useState(null);
    return (
      <DateRangePicker
        startDateId="createdAtStart"
        endDateId="createdAtEnd"
        readOnly
        showClearDates
        withPortal
        showDefaultInputIcon={false}
        hideKeyboardShortcutsPanel
        startDate={get(filter, 'value.startDate', null) ? moment(filter.value.startDate) : null}
        endDate={get(filter, 'value.endDate', null) ? moment(filter.value.endDate) : null}
        onDatesChange={({ startDate, endDate }) => onChange({ startDate, endDate })}
        firstDayOfWeek={1}
        small
        focusedInput={inputFocused}
        onFocusChange={focusedInput => setInputFocused(focusedInput)}
        displayFormat={FORMAT_STANDARD_DATE}
        startDatePlaceholderText={intl.formatMessage({ id: 'inputs.rangeStart' })}
        endDatePlaceholderText={intl.formatMessage({ id: 'inputs.rangeEnd' })}
        isOutsideRange={date => date.isAfter(moment(), 'day')}
        initialVisibleMonth={() =>
          (moment(get(filter, 'value.startDate', null)).isValid() && moment(filter.value.startDate)) ||
          (moment(get(filter, 'value.endDate', null)).isValid() && moment(filter.value.endDate)) ||
          moment().subtract(1, 'month')
        }
        renderMonthElement={({ month, onMonthSelect, onYearSelect }) => (
          <div className="d-flex justify-content-center">
            <div className="mr-1">
              <select value={month.month()} onChange={e => onMonthSelect(month, e.target.value)}>
                {moment.months().map((label, value) => (
                  <option value={value} key={label}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className="ml-1">
              <select value={month.year()} onChange={e => onYearSelect(month, e.target.value)}>
                {yearListRender(2019, new Date().getFullYear())}
              </select>
            </div>
          </div>
        )}
      />
    );
  }),
};

const UPDATE_TIMESTAMP_COLUMN = {
  headerClassName: 'react-table-headers',
  className: 'react-table-cells',
  headerStyle: { fontWeight: '500' },
  style: { fontWeight: '500' },
  minWidth: 250,
  id: 'updatedAtInterval',
  accessor: 'updatedAt',
  Header: <FormattedMessage id="adminTicketListPage.columns.updateTimestamp" />,
  Cell: rowInfo => <div>{formatDateTime(rowInfo.original.updatedAt, false)}</div>,
  Filter: injectIntl(({ filter, onChange, intl }) => {
    const [inputFocused, setInputFocused] = useState(null);
    return (
      <DateRangePicker
        startDateId="updatedAtStart"
        endDateId="updatedAtEnd"
        readOnly
        showClearDates
        withPortal
        showDefaultInputIcon={false}
        hideKeyboardShortcutsPanel
        startDate={get(filter, 'value.startDate', null) ? moment(filter.value.startDate) : null}
        endDate={get(filter, 'value.endDate', null) ? moment(filter.value.endDate) : null}
        onDatesChange={({ startDate, endDate }) => onChange({ startDate, endDate })}
        firstDayOfWeek={1}
        small
        focusedInput={inputFocused}
        onFocusChange={focusedInput => setInputFocused(focusedInput)}
        displayFormat={FORMAT_STANDARD_DATE}
        startDatePlaceholderText={intl.formatMessage({ id: 'inputs.rangeStart' })}
        endDatePlaceholderText={intl.formatMessage({ id: 'inputs.rangeEnd' })}
        isOutsideRange={date => date.isAfter(moment(), 'day')}
        initialVisibleMonth={() =>
          (moment(get(filter, 'value.startDate', null)).isValid() && moment(filter.value.startDate)) ||
          (moment(get(filter, 'value.endDate', null)).isValid() && moment(filter.value.endDate)) ||
          moment().subtract(1, 'month')
        }
      />
    );
  }),
};

const DUE_DATE_COLUMN = {
  headerClassName: 'react-table-headers',
  className: 'react-table-cells',
  headerStyle: { fontWeight: '500' },
  style: { fontWeight: '500' },
  minWidth: 250,
  id: 'dueDateInterval',
  accessor: 'dueDate',
  Header: <FormattedMessage id="adminTicketListPage.columns.dueDate" />,
  Cell: rowInfo => <div>{formatDateTime(rowInfo.original.dueDate, false)}</div>,
  Filter: injectIntl(({ filter, onChange, intl }) => {
    const [inputFocused, setInputFocused] = useState(null);

    return (
      <DateRangePicker
        startDateId="dueDateStart"
        endDateId="dueDateEnd"
        readOnly
        showClearDates
        withPortal
        showDefaultInputIcon={false}
        hideKeyboardShortcutsPanel
        startDate={get(filter, 'value.startDate', null) ? moment(filter.value.startDate) : null}
        endDate={get(filter, 'value.endDate', null) ? moment(filter.value.endDate) : null}
        onDatesChange={({ startDate, endDate }) =>
          onChange({
            startDate: startDate ? moment(startDate).set('hour', 0) : null,
            endDate: endDate
              ? moment(endDate)
                  .set('hour', 23)
                  .set('minutes', 59)
              : null,
          })
        }
        firstDayOfWeek={1}
        small
        focusedInput={inputFocused}
        onFocusChange={focusedInput => setInputFocused(focusedInput)}
        displayFormat={FORMAT_STANDARD_DATE}
        startDatePlaceholderText={intl.formatMessage({ id: 'inputs.rangeStart' })}
        endDatePlaceholderText={intl.formatMessage({ id: 'inputs.rangeEnd' })}
        isOutsideRange={() => false}
        initialVisibleMonth={() =>
          (moment(get(filter, 'value.startDate', null)).isValid() && moment(filter.value.startDate)) ||
          (moment(get(filter, 'value.endDate', null)).isValid() && moment(filter.value.endDate)) ||
          moment()
        }
      />
    );
  }),
};

export const columns = [
  TICKET_ID_COLUMN,
  REQUIRED_ACTION_COLUMN,
  FIRST_NAME_COLUMN,
  LAST_NAME_COLUMN,
  TITLE_COLUMN,
  TYPE_COLUMN,
  STATUS_COLUMN,
  CREATION_TIMESTAMP_COLUMN,
  UPDATE_TIMESTAMP_COLUMN,
  DUE_DATE_COLUMN,
];
